import { createPortal } from 'react-dom'
import { Toast } from './Toast'
import { useToast } from '@/context/ToastContext'

/* ==========================================================================
   Toast Container
   Fixed position container at bottom-right that renders active toasts
   ========================================================================== */

/**
 * Container component that renders all active toasts.
 * Position fixed at bottom-right with vertical stacking.
 * Should be placed at the root level of your app, outside of other containers.
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <ToastProvider>
 *       <Router>
 *         <Routes>...</Routes>
 *       </Router>
 *       <ToastContainer />
 *     </ToastProvider>
 *   )
 * }
 * ```
 */
export function ToastContainer() {
  const { toasts, removeToast } = useToast()

  // Render into <body> via a portal so toasts are a sibling of the Headless UI
  // modal portal (also appended to <body>). Combined with a z-index above the
  // modal's z-50, this keeps toasts visible on top of any open modal instead of
  // being painted behind it.
  return createPortal(
    <div
      className="fixed bottom-6 right-6 z-[100] flex flex-col gap-3 max-w-sm w-full pointer-events-none"
      aria-label="Notifications"
    >
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <Toast
            id={toast.id}
            variant={toast.variant}
            message={toast.message}
            visible={true}
            onDismiss={removeToast}
            duration={toast.duration}
          />
        </div>
      ))}
    </div>,
    document.body
  )
}

export default ToastContainer
